import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  orderBy, 
  addDoc, 
  updateDoc, 
  doc, 
  serverTimestamp 
} from 'firebase/firestore';
import { useAuth } from './AuthContext';

const MessagingContext = createContext();

export const useMessaging = () => {
  return useContext(MessagingContext);
};

export const MessagingProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversations, setConversations] = useState([]);

  // Fetch messages for current user
  useEffect(() => {
    if (!currentUser) {
      setMessages([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    
    // Safety timeout to ensure loading state is reset even if there are issues
    const loadingTimeout = setTimeout(() => {
      console.log("Safety timeout triggered - forcing loading state to false");
      setLoading(false);
    }, 5000);
    
    // Create queries for both received and sent messages
    // 1. Messages where current user is recipient
    const receivedMessagesQuery = query(
      collection(db, 'messages'),
      where('recipientId', '==', currentUser.uid),
      orderBy('timestamp', 'desc')
    );
    
    // 2. Messages where current user is sender
    const sentMessagesQuery = query(
      collection(db, 'messages'),
      where('senderId', '==', currentUser.uid),
      orderBy('timestamp', 'desc')
    );

    let receivedMessages = [];
    let sentMessages = [];
    let unsubscribeReceived, unsubscribeSent;
    
    // Process all messages to update state
    const processMessages = () => {
      // Combine both message lists
      const allMessages = [...receivedMessages, ...sentMessages];
      setMessages(allMessages);
      
      // Count unread messages (only from received messages)
      const unread = receivedMessages.filter(msg => !msg.read).length;
      setUnreadCount(unread);
      
      // Extract unique conversations
      const conversationMap = {};
      
      // Process received messages (messages from others)
      receivedMessages.forEach(msg => {
        const conversationId = msg.senderId;
        if (!conversationMap[conversationId]) {
          conversationMap[conversationId] = {
            id: conversationId,
            displayName: msg.senderName || 'Unknown',
            lastMessage: msg.content,
            timestamp: msg.timestamp,
            unread: !msg.read,
            isAdmin: msg.isAdminMessage || false
          };
        } else {
          // Safely compare timestamps
          const existingTimestamp = conversationMap[conversationId].timestamp;
          const msgTimestamp = msg.timestamp;
          
          let isNewer = false;
          
          try {
            // Handle Firestore timestamps, string dates, or undefined values
            const dateA = existingTimestamp ? 
                         (existingTimestamp.toDate ? existingTimestamp.toDate() : new Date(existingTimestamp)) : 
                         new Date(0);
            const dateB = msgTimestamp ? 
                         (msgTimestamp.toDate ? msgTimestamp.toDate() : new Date(msgTimestamp)) : 
                         new Date(0);
            isNewer = dateB > dateA;
          } catch (err) {
            console.error("Error comparing timestamps:", err);
            isNewer = false;
          }
          
          if (isNewer) {
            conversationMap[conversationId].lastMessage = msg.content;
            conversationMap[conversationId].timestamp = msg.timestamp;
            conversationMap[conversationId].unread = !msg.read || conversationMap[conversationId].unread;
          }
        }
      });
      
      // Process sent messages (add conversations where user initiated conversation)
      sentMessages.forEach(msg => {
        const conversationId = msg.recipientId;
        if (!conversationMap[conversationId]) {
          conversationMap[conversationId] = {
            id: conversationId,
            displayName: msg.recipientName || 'Unknown',
            lastMessage: msg.content,
            timestamp: msg.timestamp,
            unread: false,
            isAdmin: false
          };
        } else {
          // Safely compare timestamps
          const existingTimestamp = conversationMap[conversationId].timestamp;
          const msgTimestamp = msg.timestamp;
          
          let isNewer = false;
          
          try {
            // Handle Firestore timestamps, string dates, or undefined values
            const dateA = existingTimestamp ? 
                         (existingTimestamp.toDate ? existingTimestamp.toDate() : new Date(existingTimestamp)) : 
                         new Date(0);
            const dateB = msgTimestamp ? 
                         (msgTimestamp.toDate ? msgTimestamp.toDate() : new Date(msgTimestamp)) : 
                         new Date(0);
            isNewer = dateB > dateA;
          } catch (err) {
            console.error("Error comparing timestamps:", err);
            isNewer = false;
          }
          
          if (isNewer) {
            conversationMap[conversationId].lastMessage = msg.content;
            conversationMap[conversationId].timestamp = msg.timestamp;
          }
        }
      });
      
      const conversationList = Object.values(conversationMap);
      console.log("Processing messages - conversation count:", conversationList.length);
      
      setConversations(conversationList.sort((a, b) => {
        try {
          // Safely compare timestamps for sorting
          const timeA = a.timestamp ? 
                       (a.timestamp.toDate ? a.timestamp.toDate() : new Date(a.timestamp)) : 
                       new Date(0);
          const timeB = b.timestamp ? 
                       (b.timestamp.toDate ? b.timestamp.toDate() : new Date(b.timestamp)) : 
                       new Date(0);
          return timeB - timeA;
        } catch (err) {
          console.error("Error sorting conversations:", err);
          return 0;
        }
      }));
      
      // Ensure loading state is always set to false
      setLoading(false);
      clearTimeout(loadingTimeout); // Clear the timeout if normal processing completes
    };
    
    // Listen for received messages
    unsubscribeReceived = onSnapshot(receivedMessagesQuery, (snapshot) => {
      receivedMessages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      processMessages();
    });
    
    // Listen for sent messages
    unsubscribeSent = onSnapshot(sentMessagesQuery, (snapshot) => {
      sentMessages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      processMessages();
    });
    
    return () => {
      if (unsubscribeReceived) unsubscribeReceived();
      if (unsubscribeSent) unsubscribeSent();
      clearTimeout(loadingTimeout); // Clear the timeout on cleanup
    };
  }, [currentUser]);

  // Mark a message as read
  const markAsRead = async (messageId) => {
    if (!messageId) return;
    
    const messageRef = doc(db, 'messages', messageId);
    await updateDoc(messageRef, {
      read: true
    });
  };

  // Mark all messages in a conversation as read
  const markConversationAsRead = async (senderId) => {
    if (!senderId || !currentUser) return;
    
    const unreadMessages = messages.filter(
      msg => msg.senderId === senderId && !msg.read
    );
    
    const updatePromises = unreadMessages.map(msg => 
      updateDoc(doc(db, 'messages', msg.id), { read: true })
    );
    
    await Promise.all(updatePromises);
  };

  // Send a message
  const sendMessage = async (recipientId, content) => {
    if (!currentUser || !recipientId || !content.trim()) return;
    
    // Get the recipient's info to include their name (if available)
    let recipientName = 'Admin'; // Default name if we can't find it
    
    try {
      // Try to find the recipient's name in existing conversations
      const existingConversation = conversations.find(conv => conv.id === recipientId);
      if (existingConversation && existingConversation.displayName) {
        recipientName = existingConversation.displayName;
      }
    } catch (error) {
      console.error("Error getting recipient name:", error);
    }
    
    await addDoc(collection(db, 'messages'), {
      senderId: currentUser.uid,
      senderName: currentUser.displayName || 'User',
      recipientId: recipientId,
      recipientName: recipientName,
      content: content,
      timestamp: serverTimestamp(),
      read: false,
      isAdminMessage: false // Users send regular messages, admins send admin messages
    });
  };

  // Get messages for a specific conversation
  const getConversationMessages = (conversationId) => {
    if (!conversationId || !currentUser) return [];
    
    // Need to get both:
    // 1. Messages sent FROM conversationId TO currentUser
    // 2. Messages sent FROM currentUser TO conversationId
    return messages
      .filter(msg => 
        (msg.senderId === conversationId && msg.recipientId === currentUser.uid) || 
        (msg.senderId === currentUser.uid && msg.recipientId === conversationId)
      )
      .sort((a, b) => {
        // Handle potential undefined timestamps
        const timeA = a.timestamp ? new Date(a.timestamp) : new Date(0);
        const timeB = b.timestamp ? new Date(b.timestamp) : new Date(0);
        return timeA - timeB;
      });
  };

  const value = {
    messages,
    unreadCount,
    loading,
    conversations,
    selectedConversation,
    setSelectedConversation,
    markAsRead,
    markConversationAsRead,
    sendMessage,
    getConversationMessages
  };

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
};

export default MessagingContext; 